import { HeaderPositionType, ListStyleType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class CompareItemsCategory extends BaseCategory {
    get name() {
        return "Compare Items";
    }

    get type() {
        return SlideCategories.COMPARE_ITEMS;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for side-by-side or split comparison of non-numeric items, products, plans, or options. Shows features, specifications, or attributes across multiple columns for easy comparison.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a comparison slide showing items side-by-side with their features or attributes.
        Provide items to compare along with their features/characteristics.`;
    }

    getImagePrompt() {
        return false;
    }

    getImageDescription() {
        return `If the userinput already describes a specific image, just rewrite that description.
If the userinput does not suggest a specific image, describe an image that complements the userinput.

Only describe the subject and contents of the image. Do not include instructions for style, color, or composition unless specifically relevant to the content.
Make sure the prompt is clear and descriptive but not long or overly detailed (ie. "close up of the moon", "two business people in a meeting", "abstract representation of time").`;
    }

    getPromptSchema(options) {
        if (options.preserveExactText) {
            return {
                items: [{
                    title: "the exact primary text provided for the item",
                    description: "the exact secondary text provided for the item or leave empty if none",
                    imageDescription: this.getImageDescription()
                }]
            };
        } else {
            return {
                items: [{
                    title: "either a short title or a bullet point",
                    // subbullets: [{ text: "string" }],
                    description: "an optional short description or explanation for the item",
                    imageDescription: this.getImageDescription(),
                }]
            };
        }
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.items)) {
            return false;
        }
        return true;
    }

    getSlideModels(model) {
        let models = [
            this.buildCompareItems(model)
        ];
        return models;
    }

    buildCompareItems(model) {
        let builder = new SlideModelBuilder("stackDiagram", model);

        // Add header if there's a title
        if (model.slideTitle) {
            builder.createHeader({
                heading: model.slideTitle,
                position: HeaderPositionType.TOP
            });
        }

        let columnWidth = 12 / model.items.length;
        let x = 0;

        let gridLayout = {
            cells: [],
            cols: 12,
            rows: 12
        };

        const cells = [];

        for (let item of model.items) {
            gridLayout.cells.push({ x, y: 0, width: columnWidth, height: 2 });
            gridLayout.cells.push({ x, y: 2, width: columnWidth, height: 7 });
            gridLayout.cells.push({ x, y: 9, width: columnWidth, height: 3 });
            cells.push({
                componentType: "TextAndImage",
                cellColor: "none",
                childElement: {
                    text: builder.createSingleBlockText(item.title, TextStyleType.HEADING)
                }
            });
            cells.push({
                componentType: "TextAndImage",
                cellColor: "none",
                childElement: {
                    ...builder.createImage(item.imageDescription)
                }
            });
            cells.push({
                componentType: "TextAndImage",
                cellColor: "none",
                childElement: {
                    text: {
                        blocks: [
                            builder.createBlock(item.description, TextStyleType.BODY),
                            ...(item.subbullets ?? []).map(subbullet =>
                                builder.createBlock(subbullet.text, TextStyleType.BULLET_LIST, { listStyle: item.subbullets.length > 1 ? ListStyleType.BULLET : ListStyleType.TEXT, indent: 0 })
                            )
                        ].filter(b => b)
                    }
                }
            });
            x += columnWidth;
        }

        builder.createPrimary({
            gridLayout,
            items: cells,
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
